/* Periodo: lavoro fatto, avvisi mandati, incassato, e i due buchi, per un periodo a scelta.
 *
 * Dal 17 ottobre 2026. E' la schermata con cui il titolare controlla l'operato, l'incassato e la
 * fatturazione: sceglie il periodo con un tocco (anno, semestri, trimestri, anno a cavallo dal 1
 * luglio al 30 giugno, oppure due date), e se vuole un cliente. I conti stanno nel dominio
 * (`domain/periodo.py`) e qui non si rifa niente: si sceglie, si apre, si legge.
 *
 * **I due buchi sono cose diverse.** "Da fatturare" e' lavoro fatto e non ancora finito in un
 * avviso: al cliente non e' arrivato niente, quindi nessuno lo deve ancora pagare. "Da incassare" e'
 * quanto manca sugli avvisi gia' mandati. Si toccano i due numeri e la tabella sotto si apre su
 * quello che manca: nel primo caso il lavoro fermo per cliente, nel secondo chi deve pagare e quanto.
 *
 * **Ogni cifra conta alla propria data**, e la pagina lo scrive sotto i numeri: il lavoro quando e'
 * stato fatto, gli avvisi quando sono partiti, gli incassi quando sono stati versati. Senza quella
 * riga lo stesso trimestre darebbe cifre che sembrano in disaccordo e non lo sono.
 *
 * **La vedono il titolare e chi fattura** (dal 23 ottobre 2026): sono soldi dei clienti, cose che chi
 * fattura ha gia' sotto gli occhi un documento alla volta. Le righe per persona non ci sono: il server le
 * toglie a chi non ha le metriche sulle persone (`CONTROLLO_PERIODO`, vedi `core/capacita.py`), e questa
 * pagina non le mostra comunque. Il margine e i costi stanno in Gestione, solo del titolare.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { controllo, ErroreApi } from "../api";
import { useCarica } from "../carica";
import { CampoCerca } from "../componenti/CampoCerca";
import { SelettorePeriodo } from "../componenti/SelettorePeriodo";
import {
  conta,
  dataBreve,
  euro,
  eZero,
  oreEMinuti,
  somma,
  type AvvisoPeriodo,
  type ClientePeriodo,
  type LavoroNonFatturato,
  type PeriodoControllo,
} from "../dominio";
import { selezione, type Selezione } from "../periodi";
import { datiDaMostrare } from "../ultimiDati";

type Vista = "clienti" | "avvisi" | "incassi" | "da_fatturare";

const VISTE: { id: Vista; nome: string }[] = [
  { id: "clienti", nome: "Clienti" },
  { id: "avvisi", nome: "Avvisi" },
  { id: "incassi", nome: "Incassi" },
  { id: "da_fatturare", nome: "Lavoro da fatturare" },
];

const QUANTI = 15;

/** Raggruppa per cliente tenendo i gruppi nell'ordine dato da `peso` (il piu' pesante per primo). */
function perCliente<T extends { cliente_id: string; cliente: string }>(
  righe: T[],
  peso: (r: T) => number,
): { cliente_id: string; cliente: string; righe: T[]; peso: number }[] {
  const gruppi = new Map<string, { cliente_id: string; cliente: string; righe: T[]; peso: number }>();
  for (const r of righe) {
    const g = gruppi.get(r.cliente_id) ?? { cliente_id: r.cliente_id, cliente: r.cliente, righe: [], peso: 0 };
    g.righe.push(r);
    g.peso += peso(r);
    gruppi.set(r.cliente_id, g);
  }
  return [...gruppi.values()].sort((a, b) => b.peso - a.peso || a.cliente.localeCompare(b.cliente));
}

function peso(importo: string): number {
  return Number.parseFloat(importo) || 0;
}

export function Periodo() {
  const [sel, setSel] = useState<Selezione>(() => selezione("anno", new Date().getFullYear()));
  const [clienteId, setClienteId] = useState("");
  const [clienteNome, setClienteNome] = useState("");
  const [vista, setVista] = useState<Vista>("clienti");
  const [soloAperti, setSoloAperti] = useState(false);
  const [quante, setQuante] = useState({ chiave: "", n: QUANTI });
  const [esportando, setEsportando] = useState(false);
  const [erroreEsporta, setErroreEsporta] = useState<string | null>(null);

  const carica = useCarica<PeriodoControllo>(
    () => controllo.periodo(sel.dal, sel.al, clienteId || undefined),
    [sel.dal, sel.al, clienteId],
  );
  // Mentre arrivano i dati nuovi si tiene la pagina di prima, attenuata: i comandi non spariscono
  // sotto le dita, e la scelta fatta non si perde. Dopo un errore no: i numeri rimasti sono della
  // selezione precedente e sotto i filtri nuovi sembrerebbero di quella (vedi `ultimiDati.ts`).
  const [ultimo, setUltimo] = useState<PeriodoControllo | undefined>(undefined);
  useEffect(() => {
    if (carica.dati) setUltimo(carica.dati);
  }, [carica.dati]);
  const p = datiDaMostrare(carica.dati, ultimo, carica.errore);
  const aggiorno = carica.inCorso && p !== undefined;

  const buco = vista === "da_fatturare" ? "da_fatturare" : vista === "avvisi" && soloAperti ? "da_incassare" : undefined;

  function apriBuco(quale: "da_fatturare" | "da_incassare") {
    if (buco === quale) {
      setVista("clienti");
      setSoloAperti(false);
    } else if (quale === "da_fatturare") {
      setVista("da_fatturare");
    } else {
      setVista("avvisi");
      setSoloAperti(true);
    }
  }

  function scegliVista(v: Vista) {
    setVista(v);
    if (v !== "avvisi") setSoloAperti(false);
  }

  function scegliCliente(id: string, nome = "") {
    setClienteId(id);
    setClienteNome(id ? nome : "");
  }

  const voci = useMemo(() => {
    const elenco = (p?.elenco_clienti ?? []).map((c) => ({ valore: c.cliente_id, testo: c.nome }));
    if (clienteId && !elenco.some((v) => v.valore === clienteId)) {
      elenco.unshift({ valore: clienteId, testo: clienteNome || "Cliente scelto" });
    }
    return [{ valore: "", testo: "Tutti i clienti" }, ...elenco];
  }, [p?.elenco_clienti, clienteId, clienteNome]);

  async function esporta() {
    setErroreEsporta(null);
    setEsportando(true);
    try {
      const suffisso = (clienteId ? "-cliente" : "") + (buco ? `-${buco.replace("_", "-")}` : "");
      await controllo.esportaPeriodo(
        sel.dal,
        sel.al,
        clienteId || undefined,
        buco,
        `controllo-${sel.dal}-${sel.al}${suffisso}.xlsx`,
      );
    } catch (e) {
      setErroreEsporta(e instanceof ErroreApi ? e.message : "Non sono riuscito a preparare il file.");
    } finally {
      setEsportando(false);
    }
  }

  const cosaEsporta =
    (buco === "da_incassare"
      ? "solo gli avvisi non pagati"
      : buco === "da_fatturare"
        ? "solo il lavoro da fatturare"
        : "tutto il periodo") + (clienteId ? `, solo ${clienteNome || "il cliente scelto"}` : "");

  const chiaveQuante = `${vista}|${soloAperti}|${sel.dal}|${sel.al}|${clienteId}`;
  const limite = quante.chiave === chiaveQuante ? quante.n : QUANTI;
  const altri = () => setQuante({ chiave: chiaveQuante, n: limite + QUANTI });

  return (
    <section className="periodo">
      <p className="aiuto scheda-domanda">Quanto si è lavorato, fatturato e incassato in un periodo, e chi deve ancora pagare.</p>
      <div className="filtri" aria-label="Periodo e cliente">
        <SelettorePeriodo valore={sel} anni={p?.anni ?? [sel.anno]} cambia={setSel} />
        <div className="filtri-campi periodo-filtri-cliente">
          <CampoCerca
            id="periodo-cliente"
            etichetta="Cliente"
            voci={voci}
            valore={clienteId}
            cambia={(v) => scegliCliente(v, voci.find((x) => x.valore === v)?.testo)}
            nessuna="Nessun cliente con questo nome nel periodo"
          />
          <div className="periodo-esporta">
            <button type="button" className="tasto" onClick={esporta} disabled={esportando || p === undefined}>
              {esportando ? "Preparo il file" : "Esporta in Excel"}
            </button>
            <span className="narrativa">Nel file: {cosaEsporta}.</span>
          </div>
        </div>
        {erroreEsporta && (
          <p className="errore" role="alert">
            {erroreEsporta}
          </p>
        )}
      </div>

      {carica.errore && (
        <p className="errore" role="alert">
          {carica.errore}
        </p>
      )}

      {p === undefined ? (
        carica.errore ? null : <p className="attesa">Un momento.</p>
      ) : (
        <div className={aggiorno ? "periodo-corpo in-aggiornamento" : "periodo-corpo"} aria-busy={aggiorno}>
          <NumeriInTesta p={p} buco={buco} apri={apriBuco} />
          <p className="narrativa periodo-nota">
            Il lavoro conta alla data in cui è stato fatto, gli avvisi alla data di invio, gli incassi alla data del
            versamento. I due buchi non si sommano: il lavoro da fatturare non è ancora arrivato al cliente.
          </p>

          <div className="gestione-viste periodo-viste" role="tablist" aria-label="Cosa guardare">
            {VISTE.map((v) => (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={vista === v.id}
                className={vista === v.id ? "tasto tasto-principale" : "tasto"}
                onClick={() => scegliVista(v.id)}
              >
                {v.nome}
              </button>
            ))}
          </div>

          {vista === "clienti" && <TabellaClienti p={p} limite={limite} altri={altri} scegli={scegliCliente} />}
          {vista === "avvisi" && (
            <TabellaAvvisi
              p={p}
              soloAperti={soloAperti}
              cambiaSolo={setSoloAperti}
              limite={limite}
              altri={altri}
              scegli={scegliCliente}
            />
          )}
          {vista === "incassi" && <TabellaIncassi p={p} limite={limite} altri={altri} />}
          {vista === "da_fatturare" && <TabellaDaFatturare p={p} limite={limite} altri={altri} scegli={scegliCliente} />}
        </div>
      )}
    </section>
  );
}

/** I cinque numeri nell'ordine in cui il lavoro scorre: fatto, fermo, mandato, versato, ancora dovuto.
 * I due fermi e dovuti sono bottoni: toccati, aprono sotto chi e cosa. */
function NumeriInTesta({
  p,
  buco,
  apri,
}: {
  p: PeriodoControllo;
  buco: "da_fatturare" | "da_incassare" | undefined;
  apri: (quale: "da_fatturare" | "da_incassare") => void;
}) {
  const t = p.totali;
  return (
    <div className="numeri-testa numeri-testa-cinque">
      <div className="numero-testa">
        <span className="numero-testa-cifra">{oreEMinuti(t.minuti)}</span>
        <span className="numero-testa-nome">di lavoro, vale {euro(t.valore_ore)} a tariffa</span>
      </div>
      <button
        type="button"
        className="numero-testa numero-buco"
        aria-pressed={buco === "da_fatturare"}
        onClick={() => apri("da_fatturare")}
      >
        <span className="numero-testa-cifra">{euro(t.da_fatturare)}</span>
        <span className="numero-testa-nome">
          da fatturare
          {t.senza_tariffa > 0 && `, più ${conta(t.senza_tariffa, "riga senza tariffa", "righe senza tariffa")}`}
        </span>
      </button>
      <div className="numero-testa">
        <span className="numero-testa-cifra">{euro(t.importo_avvisi)}</span>
        <span className="numero-testa-nome">{conta(t.avvisi, "avviso mandato", "avvisi mandati")}</span>
      </div>
      <div className="numero-testa">
        <span className="numero-testa-cifra">{euro(t.incassato)}</span>
        <span className="numero-testa-nome">incassato nel periodo</span>
      </div>
      <button
        type="button"
        className="numero-testa numero-buco"
        aria-pressed={buco === "da_incassare"}
        onClick={() => apri("da_incassare")}
      >
        <span className="numero-testa-cifra">{euro(t.da_incassare)}</span>
        <span className="numero-testa-nome">
          da incassare, {conta(t.avvisi_aperti, "avviso", "avvisi")}
        </span>
      </button>
    </div>
  );
}

function PulsanteAltri({ totale, limite, altri }: { totale: number; limite: number; altri: () => void }) {
  if (totale <= limite) return null;
  return (
    <p>
      <button type="button" className="tasto" onClick={altri}>
        Mostra altri {QUANTI} ({totale - limite} ancora nascosti)
      </button>
    </p>
  );
}

function NomeCliente({ id, nome, scegli }: { id: string; nome: string; scegli: (id: string, nome: string) => void }) {
  return (
    <button type="button" className="tasto-nudo pratica-nome" onClick={() => scegli(id, nome)} title="Guarda solo questo cliente">
      {nome}
    </button>
  );
}

function TabellaClienti({
  p,
  limite,
  altri,
  scegli,
}: {
  p: PeriodoControllo;
  limite: number;
  altri: () => void;
  scegli: (id: string, nome: string) => void;
}) {
  const righe: ClientePeriodo[] = p.clienti;
  if (righe.length === 0) return <p className="aiuto">Niente nel periodo.</p>;
  const t = p.totali;
  return (
    <>
      <table className="tabella periodo-tabella">
        <thead>
          <tr>
            <th>Cliente</th>
            <th className="cifra colonna-secondaria">Ore</th>
            <th className="cifra">Da fatturare</th>
            <th className="cifra colonna-secondaria">Avvisi mandati</th>
            <th className="cifra colonna-secondaria">Incassato</th>
            <th className="cifra">Da incassare</th>
          </tr>
        </thead>
        <tbody>
          {righe.slice(0, limite).map((r) => (
            <tr key={r.cliente_id}>
              <td>
                <NomeCliente id={r.cliente_id} nome={r.nome} scegli={scegli} />
                {r.senza_tariffa > 0 && (
                  <span className="narrativa">{conta(r.senza_tariffa, "riga senza tariffa", "righe senza tariffa")}</span>
                )}
              </td>
              <td className="cifra colonna-secondaria">{oreEMinuti(r.minuti)}</td>
              <td className={eZero(r.da_fatturare) ? "cifra" : "cifra cella-buco"}>{euro(r.da_fatturare)}</td>
              <td className="cifra colonna-secondaria">
                {euro(r.importo_avvisi)}
                {r.avvisi > 0 && <span className="narrativa">{conta(r.avvisi, "avviso", "avvisi")}</span>}
              </td>
              <td className="cifra colonna-secondaria">{euro(r.incassato)}</td>
              <td className={eZero(r.da_incassare) ? "cifra" : "cifra cella-buco"}>{euro(r.da_incassare)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>Totale</th>
            <th className="cifra colonna-secondaria">{oreEMinuti(t.minuti)}</th>
            <th className="cifra">{euro(t.da_fatturare)}</th>
            <th className="cifra colonna-secondaria">{euro(t.importo_avvisi)}</th>
            <th className="cifra colonna-secondaria">{euro(t.incassato)}</th>
            <th className="cifra">{euro(t.da_incassare)}</th>
          </tr>
        </tfoot>
      </table>
      <PulsanteAltri totale={righe.length} limite={limite} altri={altri} />
    </>
  );
}

function RigaAvviso({
  a,
  mostraCliente,
  scegli,
}: {
  a: AvvisoPeriodo;
  mostraCliente: boolean;
  /** Se c'e', il nome del cliente e' un bottone che lo sceglie: serve quando la riga non ha sopra la testa del suo gruppo. */
  scegli?: (id: string, nome: string) => void;
}) {
  const aperto = !eZero(a.residuo);
  return (
    <tr className={aperto ? "riga-buco" : undefined}>
      <td>
        {scegli && <NomeCliente id={a.cliente_id} nome={a.cliente} scegli={scegli} />}
        <Link className="pratica-nome" to={`/fatturare/avvisi?documento=${encodeURIComponent(a.documento_id)}`}>
          {a.descrizione || "Avviso"}
        </Link>
        <span className="narrativa">
          {mostraCliente && !scegli && `${a.cliente}, `}
          {dataBreve(a.data)}
          {a.fattura && `, fattura ${a.fattura}`}
        </span>
      </td>
      <td className="cifra colonna-secondaria">{euro(a.netto)}</td>
      <td className="cifra colonna-secondaria">{euro(a.versato)}</td>
      <td className="cifra">{aperto ? euro(a.residuo) : "pagato"}</td>
    </tr>
  );
}

function TabellaAvvisi({
  p,
  soloAperti,
  cambiaSolo,
  limite,
  altri,
  scegli,
}: {
  p: PeriodoControllo;
  soloAperti: boolean;
  cambiaSolo: (v: boolean) => void;
  limite: number;
  altri: () => void;
  scegli: (id: string, nome: string) => void;
}) {
  const righe = soloAperti ? p.avvisi.filter((a) => !eZero(a.residuo)) : [...p.avvisi].reverse();
  const gruppi = soloAperti ? perCliente(righe, (a) => peso(a.residuo)) : [];
  const intestazione = (
    <>
      <label className="gestione-perdita">
        <input type="checkbox" checked={soloAperti} onChange={(e) => cambiaSolo(e.target.checked)} />
        <span>
          Solo quelli non pagati per intero ({p.totali.avvisi_aperti}): chi deve ancora pagare
        </span>
      </label>
      {soloAperti && righe.length > 0 && (
        <p>
          <span className="cifra cifra-grande">{euro(p.totali.da_incassare)}</span> da incassare su{" "}
          {conta(righe.length, "avviso", "avvisi")}, {conta(gruppi.length, "cliente", "clienti")}.
        </p>
      )}
    </>
  );
  if (righe.length === 0) {
    return (
      <>
        {intestazione}
        <p className="aiuto">{soloAperti ? "Tutti gli avvisi del periodo sono pagati." : "Nessun avviso mandato nel periodo."}</p>
      </>
    );
  }
  const colonne = (
    <thead>
      <tr>
        <th>Avviso</th>
        <th className="cifra colonna-secondaria">Netto</th>
        <th className="cifra colonna-secondaria">Versato</th>
        <th className="cifra">Manca</th>
      </tr>
    </thead>
  );
  if (!soloAperti) {
    return (
      <>
        {intestazione}
        <table className="tabella periodo-tabella">
          {colonne}
          <tbody>
            {righe.slice(0, limite).map((a) => (
              <RigaAvviso key={a.documento_id} a={a} mostraCliente />
            ))}
          </tbody>
        </table>
        <PulsanteAltri totale={righe.length} limite={limite} altri={altri} />
      </>
    );
  }
  return (
    <>
      {intestazione}
      <table className="tabella periodo-tabella">
        {colonne}
        {gruppi.slice(0, limite).map((g) =>
          // Un cliente con un avviso solo non ha bisogno di una testata che ripete la stessa cifra.
          g.righe.length === 1 ? (
            <tbody key={g.cliente_id} className="gruppo-cliente gruppo-singolo">
              <RigaAvviso a={g.righe[0]!} mostraCliente={false} scegli={scegli} />
            </tbody>
          ) : (
            <tbody key={g.cliente_id} className="gruppo-cliente">
              <tr className="testa-gruppo">
                <th colSpan={3}>
                  <NomeCliente id={g.cliente_id} nome={g.cliente} scegli={scegli} />
                </th>
                <th className="cifra">{euro(somma(g.righe.map((a) => a.residuo)))}</th>
              </tr>
              {g.righe.map((a) => (
                <RigaAvviso key={a.documento_id} a={a} mostraCliente={false} />
              ))}
            </tbody>
          ),
        )}
      </table>
      <PulsanteAltri totale={gruppi.length} limite={limite} altri={altri} />
    </>
  );
}

function TabellaIncassi({ p, limite, altri }: { p: PeriodoControllo; limite: number; altri: () => void }) {
  if (p.incassi.length === 0) return <p className="aiuto">Nessun incasso nel periodo.</p>;
  const righe = [...p.incassi].reverse();
  return (
    <>
      <table className="tabella periodo-tabella">
        <thead>
          <tr>
            <th>Data</th>
            <th>Cliente</th>
            <th className="colonna-secondaria">Modalità</th>
            <th className="cifra">Importo</th>
          </tr>
        </thead>
        <tbody>
          {righe.slice(0, limite).map((i) => (
            <tr key={i.incasso_id}>
              <td>{dataBreve(i.data)}</td>
              <td>
                <span className="pratica-nome">{i.cliente}</span>
                <span className="narrativa">
                  {i.avviso || "Avviso"}, del {dataBreve(i.data_avviso)}
                </span>
              </td>
              <td className="colonna-secondaria">{i.modalita}</td>
              <td className="cifra">{euro(i.importo)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={3}>Totale incassato ({conta(righe.length, "versamento", "versamenti")})</th>
            <th className="cifra">{euro(p.totali.incassato)}</th>
          </tr>
        </tfoot>
      </table>
      <PulsanteAltri totale={righe.length} limite={limite} altri={altri} />
    </>
  );
}

function RigaLavoro({ r }: { r: LavoroNonFatturato }) {
  return (
    <tr>
      <td>
        <Link className="pratica-nome" to={`/fatturare/da-fatturare?pratica=${encodeURIComponent(r.pratica_id)}`}>
          {r.pratica}
        </Link>
        {r.senza_tariffa > 0 && (
          <span className="narrativa">{conta(r.senza_tariffa, "riga senza tariffa", "righe senza tariffa")}</span>
        )}
      </td>
      <td className="cifra colonna-secondaria">{oreEMinuti(r.minuti)}</td>
      <td className="cifra">{euro(r.da_fatturare)}</td>
    </tr>
  );
}

function TabellaDaFatturare({
  p,
  limite,
  altri,
  scegli,
}: {
  p: PeriodoControllo;
  limite: number;
  altri: () => void;
  scegli: (id: string, nome: string) => void;
}) {
  const gruppi = perCliente(p.non_fatturato, (r) => peso(r.da_fatturare));
  if (gruppi.length === 0) return <p className="aiuto">Niente da fatturare nel periodo.</p>;
  return (
    <>
      <p>
        <span className="cifra cifra-grande">{euro(p.totali.da_fatturare)}</span> di lavoro fatto e non ancora in un
        avviso, su {conta(gruppi.length, "cliente", "clienti")}.
      </p>
      <table className="tabella periodo-tabella">
        <thead>
          <tr>
            <th>Pratica</th>
            <th className="cifra colonna-secondaria">Ore</th>
            <th className="cifra">Da fatturare</th>
          </tr>
        </thead>
        {gruppi.slice(0, limite).map((g) => (
          <tbody key={g.cliente_id} className="gruppo-cliente">
            <tr className="testa-gruppo">
              <th colSpan={2}>
                <NomeCliente id={g.cliente_id} nome={g.cliente} scegli={scegli} />
              </th>
              <th className="cifra">{euro(somma(g.righe.map((r) => r.da_fatturare)))}</th>
            </tr>
            {g.righe.map((r) => (
              <RigaLavoro key={r.pratica_id} r={r} />
            ))}
          </tbody>
        ))}
      </table>
      <PulsanteAltri totale={gruppi.length} limite={limite} altri={altri} />
    </>
  );
}
